/**
 * @module fetchers/fetch
 */

/**
 * @typedef {module:utils/object~Object} Object
 * @typedef {Object|{delay:Number,enqueue:Boolean,request:Request,signal:AbortSignal}} Context
 * @typedef {Context|{response:Response,url:String}} Fetched
 */

import defaults from '../selectors/defaults.js'
import object from '../utils/object.js'
import resolvable from '../utils/resolvable.js'
import wait from '../utils/wait.js'

const promises = []

/**
 * Fetches a request
 * @param {Context} context
 * @returns {Promise<Fetched>}
 */
export default async (
  context
) => {
  const { window } = defaults
  const { fetch, Headers } = window
  const { delay = 1000, enqueue = true, request } = context
  const [promise, { resolve, reject }] = resolvable()
  const { headers = new Headers(), signal } = request
  
  if (!request.headers) {
    request.headers = headers
  }
  
  headers.set('x-requested-with', 'XMLHttpRequest')
  
  if (enqueue) {
    promises.push(promise)
  }

  queueMicrotask(async () => {
    while (true) {
      try {
        if (!signal?.aborted) {
          const response = await fetch(request.clone())
          const url = response.url ?? request.url

          resolve(object(context, {
            response,
            url
          }))
        } else {
          reject()
        }

        break
      } catch {
        if (!signal?.aborted) {
          await wait(delay)
        } else {
          reject()
        }
      }
    }
  })

  if (enqueue) {
    await Promise.allSettled([...promises])
    promises.shift()
  }

  return promise
}
